import { useEffect, useMemo, useState } from 'react';

import type { FreshStrawStageGradeEntry } from '@/app/services/freshStrawRest';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import {
  dialogStateToEntries,
  emptyDialogState,
  entriesToDialogState,
  StageGradeDialogState,
  sumDialogState,
} from './entries';

const STAGES: ReadonlyArray<4 | 5 | 6 | 7 | 8 | 9> = [4, 5, 6, 7, 8, 9];
const GRADES: ReadonlyArray<1 | 2 | 3> = [1, 2, 3];

type StageKey = 's4' | 's5' | 's6' | 's7' | 's8' | 's9';
const stageToKey = (stage: 4 | 5 | 6 | 7 | 8 | 9): StageKey =>
  `s${stage}` as StageKey;

export interface FreshStrawStageGradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short label shown in the dialog title — e.g. the mating name. */
  rowLabel?: string;
  /** Existing entries for the row being edited; null/undefined starts empty. */
  initialEntries?: FreshStrawStageGradeEntry[] | null;
  onSave: (entries: FreshStrawStageGradeEntry[]) => void;
}

export function FreshStrawStageGradeDialog({
  open,
  onOpenChange,
  rowLabel,
  initialEntries,
  onSave,
}: FreshStrawStageGradeDialogProps) {
  const [state, setState] = useState<StageGradeDialogState>(emptyDialogState);

  // Reseed the dialog state whenever it opens so reopening shows the saved
  // entries for the current row.
  useEffect(() => {
    if (open) {
      setState(entriesToDialogState(initialEntries ?? null));
    }
  }, [open, initialEntries]);

  const total = useMemo(() => sumDialogState(state), [state]);

  const updateCell = (
    grade: 1 | 2 | 3,
    stage: 4 | 5 | 6 | 7 | 8 | 9,
    raw: string,
  ) => {
    const next = raw === '' ? null : Math.max(0, Math.floor(Number(raw) || 0));
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) =>
        row.grade === grade ? { ...row, [stageToKey(stage)]: next } : row,
      ),
    }));
  };

  const updateZeroBucket = (raw: string) => {
    const next = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
    setState((prev) => ({ ...prev, zeroBucket: next }));
  };

  const handleClear = () => setState(emptyDialogState());

  const handleSave = () => {
    onSave(dialogStateToEntries(state));
    onOpenChange(false);
  };

  const gradeRow = (grade: 1 | 2 | 3) => {
    const row = state.rows.find((r) => r.grade === grade);
    const rowTotal = STAGES.reduce(
      (sum, s) => sum + (Number(row?.[stageToKey(s)]) || 0),
      0,
    );

    return (
      <tr key={grade}>
        <th
          scope="row"
          className="text-muted-foreground bg-muted/40 sticky left-0 px-2 text-right text-xs font-semibold"
        >
          G{grade}
        </th>
        {STAGES.map((stage) => {
          const value = row?.[stageToKey(stage)];
          return (
            <td key={stage} className="p-1">
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={value ?? ''}
                onChange={(e) => updateCell(grade, stage, e.target.value)}
                aria-label={`Grade ${grade} stage ${stage}`}
                className="h-9 text-center tabular-nums"
              />
            </td>
          );
        })}
        <td className="text-muted-foreground bg-muted/30 px-2 text-right text-xs tabular-nums">
          {rowTotal}
        </td>
      </tr>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Stage &amp; Grade{rowLabel ? ` — ${rowLabel}` : ''}
          </DialogTitle>
          <DialogDescription>
            Enter embryo counts for each stage/grade combination. The grand
            total updates as you type; use{' '}
            <span className="font-semibold">Uncategorized</span> for embryos
            that don&apos;t have a specific stage/grade.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[28rem] border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-muted-foreground bg-muted/40 sticky left-0 w-10 px-2 text-right text-xs font-semibold"
                  >
                    &nbsp;
                  </th>
                  {STAGES.map((stage) => (
                    <th
                      key={stage}
                      scope="col"
                      className="text-muted-foreground px-1 text-center text-xs font-semibold"
                    >
                      Stage&nbsp;{stage}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="text-muted-foreground w-14 px-2 text-right text-xs font-semibold"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>{GRADES.map(gradeRow)}</tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex max-w-xs flex-col gap-1.5">
              <Label
                htmlFor="fresh-straw-zero-bucket"
                className="text-muted-foreground text-xs"
              >
                Uncategorized (zero bucket)
              </Label>
              <Input
                id="fresh-straw-zero-bucket"
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={state.zeroBucket || ''}
                onChange={(e) => updateZeroBucket(e.target.value)}
                className="tabular-nums"
              />
            </div>
            <div
              className={cn(
                'text-muted-foreground text-xs tabular-nums sm:text-right',
                total > 0 && 'text-foreground font-semibold',
              )}
              data-testid="stage-grade-total"
            >
              Grand total: {total}
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleClear}>
            Clear
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
